/**
 * The green-check checklist (v11.html `renderSrcs()` 372–376, CSS 114–117).
 *
 * It lists only what is actually in context. The prototype had toggles that
 * faked an unconnected Stripe; here the server says what the model received and
 * the list is exactly that — if ingest truncated the deck at twenty slides, the
 * row says twenty.
 *
 * What the server sends is the **citation registry**, not a checklist: one
 * entry per deck slide and one per LinkedIn profile, each labelled for use
 * inside an answer ("on the deck, slide 11, deck dated …"). Nineteen slide rows
 * is a debug dump, so `toSourceChecklist` collapses it to one row per kind and
 * names each row in the reader's words.
 *
 * The detail line is what turns a list into a credibility claim, and it carries
 * one deliberate edit: the prototype's Stripe copy said "Verified, not
 * founder-stated". "Verified" is a banned word on this surface (§7) — the
 * honest claim is that the number was machine-pulled.
 */

import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip'
import { cn } from '@workspace/ui/lib/utils'
import { Check, ClipboardList, CreditCard, Info, Linkedin, Mic, Presentation } from 'lucide-react'
import { SOURCE_PILL } from '../lib/source-pill'
import type { ChatSource } from '../types/chat.types'

/** The server's `kind` vocabulary, as `profile-chat-context.ts` defines it. */
export const SOURCE_KIND = {
  DECK: 'deck',
  LINKEDIN: 'linkedin',
  RECORD: 'record',
  STRIPE: 'stripe',
  RECORDED_ANSWERS: 'recorded_answers',
  DOCUMENT: 'document',
} as const

const SOURCE_DETAIL: Record<string, string> = {
  deck: 'The current pitch deck, read as images so charts, tables and screenshots are legible rather than OCR text.',
  linkedin:
    'Scraped profile: full role history with durations, education, certifications and honours.',
  record:
    'Everything the founder has published on this profile, with the date it was last updated.',
  stripe: 'Revenue pulled directly from the payment processor. Machine-pulled, not founder-stated.',
  recorded_answers:
    "The founder's own answers, spoken live under questioning by real investors. Verbatim, and published with his approval. The investors' side is never stored.",
  document:
    'The other PDFs on this profile, read as text transcripts: their figures and prose, not their charts or images.',
}

export interface SourceChecklistRow {
  kind: string
  label: string
  detail: string
}

/**
 * One row per kind, in the order the server listed them. An unknown kind is
 * kept rather than dropped — a new source the client has not learned about yet
 * is still something the model read, and hiding it would understate the answer.
 */
export function toSourceChecklist(
  sources: readonly ChatSource[],
  slideCount: number,
  /** Documents on the link besides the deck, read or not. */
  documentCount = 0
): SourceChecklistRow[] {
  const rows: SourceChecklistRow[] = []
  const seen = new Set<string>()
  const perKind = new Map<string, number>()
  for (const source of sources) perKind.set(source.kind, (perKind.get(source.kind) ?? 0) + 1)
  for (const source of sources) {
    if (seen.has(source.kind)) continue
    seen.add(source.kind)
    rows.push({
      kind: source.kind,
      label: checklistLabel(source, slideCount, perKind.get(source.kind) ?? 1, documentCount),
      detail: checklistDetail(source.kind, perKind.get(source.kind) ?? 1, documentCount),
    })
  }
  return rows
}

/**
 * The documents row is honest about coverage: a link with three files of which
 * the chat can read two says "2 of 3", so an investor never assumes a file was
 * read when it was not. `documentCount` comes from the server; the model's own
 * registry can only say how many it DID read.
 */
function documentsLabel(read: number, onLink: number): string {
  const total = Math.max(onLink, read)
  if (read === total) {
    return read === 1 ? 'Reads the 1 document on file' : `Reads all ${read} documents on file`
  }
  return `Reads ${read} of ${total} documents on file`
}

function checklistDetail(kind: string, count: number, documentCount: number): string {
  const detail = SOURCE_DETAIL[kind] ?? ''
  if (kind === SOURCE_KIND.DOCUMENT && documentCount > count) {
    return `${detail} The others have no text transcript yet.`
  }
  return detail
}

function checklistLabel(
  source: ChatSource,
  slideCount: number,
  count: number,
  documentCount: number
): string {
  switch (source.kind) {
    case SOURCE_KIND.DOCUMENT:
      return documentsLabel(count, documentCount)
    case SOURCE_KIND.DECK:
      return `Reads the deck, ${slideCount} slides`
    case SOURCE_KIND.LINKEDIN:
      return 'LinkedIn'
    case SOURCE_KIND.RECORD:
      return 'The profile record'
    case SOURCE_KIND.STRIPE:
      return 'Stripe, live connection'
    case SOURCE_KIND.RECORDED_ANSWERS:
      return "The founder's recorded answers"
    default:
      // The server's own label. It reads as prose inside an answer rather than
      // as a checklist row, which is exactly why the known kinds are renamed —
      // but it is accurate, and accurate beats absent.
      return source.label
  }
}

export interface AskProfileSourcesProps {
  rows: readonly SourceChecklistRow[]
  /**
   * Kept for the caller's layout switch. The detail itself now always lives
   * behind the (i), so this no longer decides whether the copy exists — only
   * whether the row is the roomy pre-answer one.
   */
  showDetail: boolean
  className?: string
}

export function AskProfileSources({ rows, showDetail, className }: AskProfileSourcesProps) {
  if (rows.length === 0) return null

  return (
    <div className={cn('min-w-[200px] pt-1', className)}>
      {/* A row of green ticks reads as "these checks passed" — a validation
          claim this surface must never make. The eyebrow plus a per-source
          glyph says what it actually is: the material the answer is built
          from. The green stays, because a source only appears here when it is
          genuinely connected and read. */}
      <p className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Answers are built from
      </p>
      {/* Same pill vocabulary as the reading pips and the "N sources read"
          footer, so one shape carries a source through all three moments:
          what WILL be read, what IS being read, what WAS read. Three different
          treatments for the same three sources is what made the connection
          invisible. */}
      {/* Stacked, not wrapped: one source per line, as the prototype had it.
          A wrapped row reads as tags on a single claim; a column reads as a
          list of the things that were read, which is what it is. `items-start`
          keeps each pill the width of its own label rather than the column. */}
      <ul className="flex flex-col items-start gap-1.5">
        {rows.map((row) => (
          <li key={row.kind}>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* The whole pill is the trigger, not just the glyph: a 14px
                    target is unusable on the phones most of this traffic
                    arrives on. A button, so it is reachable by keyboard too. */}
                <button
                  type="button"
                  aria-label={row.detail === '' ? row.label : `${row.label} — what this means`}
                  className={cn(
                    SOURCE_PILL,
                    'flex cursor-help items-center gap-1.5 outline-none transition-colors hover:border-emerald-500/70 focus-visible:ring-2 focus-visible:ring-primary'
                  )}
                >
                  <SourceIcon kind={row.kind} />
                  {row.label}
                  {row.detail !== '' && <Info className="size-3 opacity-60" aria-hidden />}
                </button>
              </TooltipTrigger>
              {row.detail !== '' && (
                <TooltipContent side={showDetail ? 'right' : 'left'} className="max-w-[260px]">
                  {row.detail}
                </TooltipContent>
              )}
            </Tooltip>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** One glyph per kind, so the pill says what the material IS, not merely "yes". */
function SourceIcon({ kind }: { kind: string }) {
  const className = 'size-3 flex-none'
  switch (kind) {
    case SOURCE_KIND.DECK:
      return <Presentation className={className} aria-hidden />
    case SOURCE_KIND.LINKEDIN:
      return <Linkedin className={className} aria-hidden />
    case SOURCE_KIND.RECORD:
      return <ClipboardList className={className} aria-hidden />
    case SOURCE_KIND.STRIPE:
      return <CreditCard className={className} aria-hidden />
    case SOURCE_KIND.RECORDED_ANSWERS:
      return <Mic className={className} aria-hidden />
    default:
      return <Check className={className} aria-hidden />
  }
}
